import { useCallback, useState } from 'react';
import { TrainerMemberService } from '../../../../lib/trainerMemberService';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';

function assignMemberDisplayName(member: { member?: { email?: string | null } | null }): string {
  const profile = member.member as Parameters<typeof displayNameFromProfile>[0] | undefined;
  return displayNameFromProfile(profile) || member.member?.email || '';
}

function sortAssignMembersAlphabetically(members: any[]): any[] {
  return [...members].sort((a, b) =>
    assignMemberDisplayName(a).localeCompare(assignMemberDisplayName(b), undefined, { sensitivity: 'base' }),
  );
}

/** Shared active-member list for assign plan + assign workout modals (Library). */
export function useAssignMembersCache(profileId: string | undefined) {
  const [assignMembers, setAssignMembers] = useState<any[]>([]);
  const [loadingAssignMembers, setLoadingAssignMembers] = useState(false);

  const loadAssignMembersIfNeeded = useCallback(async (): Promise<string | null> => {
    if (assignMembers.length > 0 || !profileId) return null;
    try {
      setLoadingAssignMembers(true);
      const trainerMembers = await TrainerMemberService.getTrainerMembers(profileId);
      const activeMembers = trainerMembers.filter((m) => m.status === 'active' && m.member);
      setAssignMembers(sortAssignMembersAlphabetically(activeMembers));
      return null;
    } catch (err) {
      console.error('Error loading members:', err);
      return 'Failed to load members';
    } finally {
      setLoadingAssignMembers(false);
    }
  }, [assignMembers.length, profileId]);

  return {
    assignMembers,
    loadingAssignMembers,
    loadAssignMembersIfNeeded,
  };
}

export type AssignMembersCache = ReturnType<typeof useAssignMembersCache>;
